import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { aiCreditsService, reais, CANAL_RECARGA } from '../../services/aiCreditsService';

/**
 * Volta do Stripe depois da compra de créditos de IA. Esta página abre na aba do pagamento:
 * confirma a compra, avisa a aba onde a pessoa estava trabalhando (que atualiza o saldo sozinha)
 * e pede para fechar esta aba. Nada do trabalho em andamento é tocado.
 */
const AiCreditsReturnPage: React.FC = () => {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<'confirmando' | 'ok' | 'cancelado' | 'erro'>('confirmando');
  const [disponivel, setDisponivel] = useState<number | null>(null);
  const [mensagem, setMensagem] = useState('');
  const feito = useRef(false);

  useEffect(() => {
    if (feito.current) return;
    feito.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessao = params.get('ai_credit_session');
    if (params.get('cancelled') || !sessao) { setEstado('cancelado'); return; }
    window.history.replaceState({}, '', window.location.pathname);
    aiCreditsService.confirm(sessao)
      .then((r) => {
        setDisponivel(r.summary.available_cents);
        setEstado('ok');
        try {
          const canal = new BroadcastChannel(CANAL_RECARGA);
          canal.postMessage({ type: 'recharged', available_cents: r.summary.available_cents });
          canal.close();
        } catch { /* a outra aba atualiza ao receber o foco */ }
      })
      .catch((e) => { setMensagem(e?.response?.data?.message || ''); setEstado('erro'); });
  }, []);

  const fechar = () => { window.close(); };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 p-6 text-center shadow">
        {estado === 'confirmando' && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary-500" />
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t('aiCredits.confirming', 'Confirmando o pagamento…')}</p>
          </>
        )}
        {estado === 'ok' && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
            <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">{t('aiCredits.returnOk', 'Créditos de IA adicionados')}</h1>
            {disponivel != null && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{t('aiCredits.returnAvailable', 'Disponível agora: {{valor}}', { valor: reais(disponivel) })}</p>}
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{t('aiCredits.returnClose', 'Pode fechar esta aba e voltar para a outra: o que você estava fazendo continua lá, já com o saldo novo.')}</p>
          </>
        )}
        {estado === 'cancelado' && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-gray-400" />
            <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">{t('aiCredits.returnCancelled', 'Compra cancelada')}</h1>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{t('aiCredits.returnCancelledClose', 'Nada foi cobrado. Pode fechar esta aba.')}</p>
          </>
        )}
        {estado === 'erro' && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-500" />
            <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">{t('aiCredits.returnError', 'Não conseguimos confirmar o pagamento')}</h1>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{mensagem || t('aiCredits.returnErrorHelp', 'Se o valor foi cobrado, os créditos entram em alguns minutos. Pode fechar esta aba.')}</p>
          </>
        )}
        {estado !== 'confirmando' && (
          <button type="button" onClick={fechar} className="mt-5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
            {t('aiCredits.closeTab', 'Fechar esta aba')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AiCreditsReturnPage;
